import { Cell, Grid } from 'bold-ui'
import { useErrorHandler } from 'components/error'
import {
  DefaultFormFooter,
  EnderecoFieldGroup,
  Form,
  FormPrompt,
  FormRenderProps,
  simNaoEnumToBoolean,
  SimNaoRadioGroupField,
} from 'components/form'
import { useIsLotacaoActiveQuery, useSalvarCidadaoFciMutation } from 'graphql/hooks.generated'
import { useServerTime } from 'hooks/useServerTime'
import React, { useMemo } from 'react'
import { useHistory } from 'react-router'
import { isUndefinedOrNull } from 'util/checks'
import { metaPath } from 'util/metaPath'

import { createEnderecoCalculator } from '../create/calculator-enderecoCidadao'
import { CompartilhamentoProntuarioFieldGroup } from '../create/CompartilhamentoProntuarioFieldGroup'
import { createCidadaoFciCalculator } from './calculator-cidadaoFci'
import { CondicoesSaudeAutorreferidasSection } from './components/CondicoesSaudeAutorreferidasSection'
import { ContatosSection } from './components/ContatosSection'
import { DadosPessoaisSection } from './components/DadosPessoaisSection'
import { InformacoesSociodemograficasSection } from './components/InformacoesSociodemograficasSection'
import { InformacoesSocioEconomicasSection } from './components/InformacoesSocioEconomicasSection'
import { ResponsabilidadeAcompanhamentoSection } from './components/ResponsabilidadeAcompanhamentoSection'
import { SituacaoRuaSection } from './components/SituacaoRuaSection'
import { convertToCidadaoFciInput } from './converter-cidadaoFci'
import { CidadaoFciFormModel } from './model-cidadaoFci'
import { cidadaoFciValidator } from './validator-cidadaoFci'

interface CidadaoFciFormProps {
  initialValues?: CidadaoFciFormModel
}

const meta = metaPath<CidadaoFciFormModel>()

export function CidadaoFciForm({ initialValues }: CidadaoFciFormProps) {
  const [save, { loading }] = useSalvarCidadaoFciMutation()
  const handleRejection = useErrorHandler()

  const decorators = useMemo(() => [createCidadaoFciCalculator(meta), createEnderecoCalculator(meta)], [])
  const serverTime = useServerTime().getServerTimeNow()
  const history = useHistory()

  // TODO NP - Deve ser feito o redirecionamento correto em issus posteriores.
  const handleSubmit = (values: CidadaoFciFormModel) =>
    save({ variables: { input: convertToCidadaoFciInput(values) } })
      .then(() => {
        setTimeout(() => history.push(`/cidadao`))
      })
      .catch(handleRejection)

  const responsavelAcompanhamento = initialValues?.responsabilidadeAcompanhamento?.responsavel

  const { data: isLotacaoActiveData } = useIsLotacaoActiveQuery({
    fetchPolicy: 'network-only',
    variables: {
      input: {
        cnes: responsavelAcompanhamento?.lotacao?.unidadeSaude?.cnes,
        cns: responsavelAcompanhamento?.lotacao?.profissional?.cns,
        cbo2002: responsavelAcompanhamento?.lotacao?.cbo?.cbo2002,
        ine: responsavelAcompanhamento?.lotacao?.equipe?.ine,
        userLocalidadeId: responsavelAcompanhamento?.lotacao?.unidadeSaude?.endereco?.municipio?.id,
      },
    },
    // TODO(@NP): Skip da direita para nao estourar erro, será reavaliado em uma issue posterior
    skip: !responsavelAcompanhamento || isUndefinedOrNull(responsavelAcompanhamento?.lotacao?.unidadeSaude?.cnes),
  })

  const renderForm = (formProps: FormRenderProps<CidadaoFciFormModel>) => {
    const values = formProps.values
    const cidadaoFaleceu = values?.dadosPessoais?.faleceu

    return (
      <Grid>
        <FormPrompt />
        <Cell size={12}>
          <ResponsabilidadeAcompanhamentoSection
            meta={meta.responsabilidadeAcompanhamento}
            values={formProps.values.responsabilidadeAcompanhamento}
            cidadaoFaleceu={cidadaoFaleceu}
            isLotacaoActive={isLotacaoActiveData?.isLotacaoActive}
          />
        </Cell>
        <Cell size={12}>
          <DadosPessoaisSection
            meta={meta.dadosPessoais}
            values={values.dadosPessoais}
            serverTime={serverTime}
            isEditCnsEnabled={!(!!initialValues?.id && !isUndefinedOrNull(initialValues?.dadosPessoais?.cns))}
          />
        </Cell>
        <Cell size={12}>
          <ContatosSection
            meta={meta.contatos}
            nacionalidade={values.dadosPessoais?.nacionalidadeCidadao?.nacionalidade}
          />
        </Cell>
        <Cell size={12}>
          <EnderecoFieldGroup
            name={meta.endereco}
            formProps={formProps}
            formModelValue={values}
            renderPais
            renderHeading
          />
        </Cell>
        <Cell size={12}>
          <SimNaoRadioGroupField name={meta.statusSituacaoRua} label='Cidadão em situação de rua?' required />
        </Cell>
        {simNaoEnumToBoolean[values?.statusSituacaoRua] && (
          <Cell size={12}>
            <SituacaoRuaSection meta={meta.situacaoRua} values={values?.situacaoRua} />
          </Cell>
        )}
        <Cell size={12}>
          <InformacoesSociodemograficasSection
            meta={meta.informacoesSociodemograficas}
            values={values.informacoesSociodemograficas}
          />
        </Cell>
        <Cell size={12}>
          <InformacoesSocioEconomicasSection
            meta={meta.informacoesSocioEconomicas}
            values={values?.informacoesSocioEconomicas}
          />
        </Cell>
        <Cell size={12}>
          <CondicoesSaudeAutorreferidasSection
            meta={meta.condicoesSaudeAutorreferidas}
            values={values?.condicoesSaudeAutorreferidas}
            sexo={values?.dadosPessoais?.sexo}
          />
        </Cell>
        <Cell size={12}>
          <CompartilhamentoProntuarioFieldGroup renderHeading name={meta} />
        </Cell>
        <Cell size={12}>
          <DefaultFormFooter handleSubmit={formProps.handleSubmit} loading={loading} />
        </Cell>
      </Grid>
    )
  }

  return (
    <Form<CidadaoFciFormModel>
      initialValues={initialValues}
      decorators={decorators}
      onSubmit={handleSubmit}
      render={renderForm}
      validate={cidadaoFciValidator()}
    />
  )
}
